import { useIsomorphicLayoutEffect } from '@findigs/helpers/src/hooks/use-isomorphic-layout-effect';
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import type { JSX } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './toast';
import type { ToastAction, ToastVariant } from './toast';
import { styles } from './toast-provider.module.scss';

export type ToastMessageInput = {
  variant: ToastVariant;
  text: string | JSX.Element;
  action?: ToastAction;
  withClose?: boolean;
};

type ToastContextType = {
  addToast: (
    msg: ToastMessageInput,
    ref?: React.RefObject<HTMLElement>
  ) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

type ToastMessage = ToastMessageInput & { id: number };

export type ToastProviderProps = {
  children: React.ReactNode;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const counterRef = useRef(0);
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const [portalRef, setPortalRef] =
    useState<React.RefObject<HTMLElement> | null>(null);

  const addToast = useCallback(
    (
      msg: ToastMessageInput,
      ref: React.RefObject<HTMLElement> | null = null
    ) => {
      if (ref !== portalRef) {
        setPortalRef(ref);
        setMessages([{ ...msg, id: counterRef.current++ }]);
      } else {
        setMessages((prev) => [...prev, { ...msg, id: counterRef.current++ }]);
      }
    },
    [portalRef]
  );

  const removeToast = useCallback((id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // this is so that we don't render the portal on the server or
  // before the client finished hydrating (respecting the async boundary)
  const [isHydrated, setIsHydrated] = useState(false);
  useIsomorphicLayoutEffect(() => {
    startTransition(() => {
      setIsHydrated(true);
    });
  }, []);

  const portal =
    isHydrated &&
    createPortal(
      <div className={styles}>
        {messages.map(({ id, variant, text, action, withClose }) => (
          <Toast
            key={id}
            variant={variant}
            action={action}
            withClose={withClose}
            open
            /* override fixed positioning from individual Toast */
            style={{
              position: 'static',
              transform: 'none',
              marginTop: '0',
              pointerEvents: 'auto',
            }}
            onClose={() => removeToast(id)}
          >
            {text}
          </Toast>
        ))}
      </div>,
      portalRef?.current ?? document.body
    );

  return (
    <ToastContext.Provider value={{ addToast }}>
      {portal}
      {children}
    </ToastContext.Provider>
  );
};
